import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import { APIActions, ApiButton, Box, Button, CustomDialog, Typography } from 'web-library';
import TransactionAuditLogUpdatedDetails from 'web-library/components/TransactionAuditLogUpdatedDetails';
import {
  AdminProfileResponse,
  CandidateProfileResponse,
  CoordinatorProfileResponse,
} from 'web-library/models/AccountManagementConfiguration';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { REQUEST_TYPES } from 'web-library/models/Request';
import { UploadedFile } from 'web-library/models/UploadedFile';
import TransactionLogModal from '../../../components/TransactionLogModal';
import { CreateEditCandidateSchemaData } from '../CreateEditViewCandidateAccountDetails/CreateEditCandidateSchema';
import { useStyles } from './index';

interface AccountManagementMiscellaneousComponentsProps {
  errorMessage: string;
  uploadFileErrorMessage: string;
  apiButtonStatus: MutationStatus;
  authorities?: string[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  isEdit: boolean;
  isEditState: boolean;
  setUploadFileErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  profileDetails?: AdminProfileResponse | CandidateProfileResponse | CoordinatorProfileResponse;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  setIsEditState: React.Dispatch<React.SetStateAction<boolean>>;
  formHook?: UseFormReturn<CreateEditCandidateSchemaData>;
  handleBackToListing: () => void;
  deleteAccountModalMessage: string;
  setDeleteAccountModalMessage: React.Dispatch<React.SetStateAction<string>>;
  setOpenConfirmChangesModal: React.Dispatch<React.SetStateAction<boolean>>;
}
const AccountManagementMiscellaneousComponents = ({
  errorMessage,
  uploadFileErrorMessage,
  apiButtonStatus,
  authorities,
  setFiles,
  isEdit,
  isEditState,
  setUploadFileErrorMessage,
  profileDetails,
  setErrorMessage,
  setIsEditState,
  formHook,
  handleBackToListing,
  deleteAccountModalMessage,
  setDeleteAccountModalMessage,
  setOpenConfirmChangesModal,
}: AccountManagementMiscellaneousComponentsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [openTransactionLogModal, setOpenTransactionLogModal] = useState<boolean>(false);

  const hasEditPermission =
    authorities && authorities.includes(AUTHORITY_NAMES.CP_ADMIN_USER_ACCOUNTS_EDIT);

  const hasDeletePermission =
    authorities && authorities.includes(AUTHORITY_NAMES.CP_ADMIN_USER_ACCOUNTS_DELETE);

  const createUpdateButtonLabel = t(
    isEdit ? 'admin->>UserAccountManagement.Update' : 'admin->>UserAccountManagement.Create'
  );

  return (
    <>
      {isEdit && (
        <TransactionAuditLogUpdatedDetails
          updatedAt={profileDetails?.requestDetail?.updatedAt as string}
          updatedBy={profileDetails?.requestDetail?.updatedBy as string}
          handleOpenLogModal={() => setOpenTransactionLogModal(true)}
          label={t('admin->>UserAccountManagement.ViewTransactionLog')}
        />
      )}
      {errorMessage && (
        <Typography className={classes.errorText} variant="body2">
          {errorMessage}
        </Typography>
      )}
      {uploadFileErrorMessage && (
        <Typography className={classes.errorText} variant="body2">
          {uploadFileErrorMessage}
        </Typography>
      )}
      {isEditState ? (
        <Box className={classes.formButtonContainer}>
          <Button
            className={classes.backButton}
            type="button"
            color="secondary"
            size="medium"
            variant="outlined"
            onClick={() => {
              if (!isEdit && isEditState) {
                handleBackToListing();
              } else if (isEdit && isEditState) {
                setIsEditState(false);
                setFiles([]);
                formHook?.reset();
                setErrorMessage('');
                setUploadFileErrorMessage('');
              }
            }}
          >
            {t('admin->>UserAccountManagement.Cancel')}
          </Button>
          {(hasDeletePermission || hasEditPermission) && (
            <ApiButton
              type="submit"
              color="secondary"
              status={apiButtonStatus}
              className={classes.buttonContainer}
            >
              {createUpdateButtonLabel}
            </ApiButton>
          )}
        </Box>
      ) : (
        <Box className={classes.formButtonContainer}>
          <Button
            className={classes.backButton}
            type="button"
            color="secondary"
            size="medium"
            variant="outlined"
            onClick={() => {
              if (isEditState) setIsEditState(false);
              else handleBackToListing();
            }}
          >
            {t('admin->>UserAccountManagement.BackToList')}
          </Button>
          {(hasDeletePermission || hasEditPermission) && (
            <ApiButton
              type="submit"
              color="secondary"
              status={apiButtonStatus}
              className={classes.buttonContainer}
            >
              {t('admin->>UserAccountManagement.ConfirmAction')}
            </ApiButton>
          )}
        </Box>
      )}
      {openTransactionLogModal && (
        <TransactionLogModal
          modalState={openTransactionLogModal}
          setOpenModalState={setOpenTransactionLogModal}
          requestType={REQUEST_TYPES.ACCOUNT_MANAGEMENT}
        />
      )}

      <CustomDialog
        open={!!deleteAccountModalMessage}
        title={t('admin->>UserAccountManagement.DeleteAccountDialogTitle')}
        message={deleteAccountModalMessage}
        action={
          <APIActions
            onClose={() => setDeleteAccountModalMessage('')}
            apiCall={() => {
              setDeleteAccountModalMessage('');
              setOpenConfirmChangesModal(true);
            }}
            cancelString={t('admin->>UserAccountManagement.Cancel')}
            apiString={t('admin->>UserAccountManagement.Proceed')}
          />
        }
      />
    </>
  );
};

export default AccountManagementMiscellaneousComponents;
